import { AutoForm, AutoFormInputField, Button } from "@9amhealth/shared";
import type { CSSProperties, FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import translate from "src/lib/translate";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { toast } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import Loader from "src/ui/components/Loader/Loader";
import Track from "src/ui/components/Track/Track";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";
import ErrorBox from "../StyledComponents/ErrorBox";
import { useBlocNext } from "@blac/react";

const PronounsSchema = z.string().max(20).optional();

interface ChangePronounsProps {
  onSuccess?: () => void;
  onError?: (errorCode: string) => void;
}

const ChangePronouns: FC<ChangePronounsProps> = ({ onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<TranslationKey>();
  const navigate = useNavigate();
  const [, { updateUserPreferences }] = useBlocNext(UserPreferencesCubit);

  const handleSubmit = (data: { pronouns?: string }) => {
    setLoading(true);
    setErrorCode(undefined);
    const { pronouns } = data;

    void updateUserPreferences({
      [UserPreferenceKeys.userPronouns]: pronouns
    })
      .then(() => {
        onSuccess?.();
        navigate(createAppPath(Path.profile), { replace: true });
        toast.success("pronounsUpdated");
      })
      .catch(() => {
        setErrorCode("error_generic");
        onError?.("error_generic");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      style={
        {
          "--section-max-width": `${APP_CONTENT_WIDTH_WITHOUT_PADDING}px`
        } as CSSProperties
      }
    >
      <Track event={TrackEvent.changePronouns} type={TrackType.start} />

      <nine-spacer s="xl"></nine-spacer>
      <nine-heading>
        <h3 className="as-h4-large">
          <Translate msg="setPronouns" />
        </h3>
        <p className="color-c-80">
          <Translate msg="setPronouns.subtitle" />
        </p>
      </nine-heading>

      <nine-spacer s="xl"></nine-spacer>

      <Loader active={loading}>
        <AutoForm
          schema={z.object({
            pronouns: PronounsSchema
          })}
          onSubmit={handleSubmit}
          onChange={() => setErrorCode(undefined)}
        >
          <AutoFormInputField label={translate("pronouns")} name="pronouns" />
          {errorCode && (
            <>
              <nine-spacer s="sm"></nine-spacer>
              <ErrorBox>
                <Translate msg={errorCode} />
              </ErrorBox>
            </>
          )}
          <nine-spacer s="sm"></nine-spacer>
          <Button type="submit" theme="sunrise" style={{ margin: "auto" }}>
            <Translate msg={"save"} />
          </Button>
        </AutoForm>
      </Loader>
    </div>
  );
};

export default ChangePronouns;
